'use client'

import { CloseIcon } from '../../icons'
import React, { createRef, RefObject, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useIsFirstRender } from '../../../hooks/useIsFirstRender'
import useOnClickOutside from '../../../hooks/useOutsideClick'
import { TextTypes } from '../../../types'
import { Backdrop, Button, ButtonVariants, Text } from '../../atoms'
import { ModalSizes } from './types/modalTypes'

export interface ModalProps {
    title?: string | React.ReactNode
    size?: ModalSizes
    children?: React.ReactNode
    footer?: React.ReactNode
    /** Place component next to modal title, i.e. a language switch */
    headerComponent?: React.ReactNode
    canBeClosed?: boolean
    isModalVisible: boolean
    setModalVisibility: React.Dispatch<React.SetStateAction<boolean>>
    hasBackdrop?: boolean
    stackedButtons?: boolean
    fullContentWidth?: boolean
    onHide?: () => void
    onShow?: () => void
}

const DEFAULT_MODAL_SIZE = ModalSizes.MD
const MODAL_SCALE_INITIAL_CLASS = 'scale-75'
const MODAL_OPACITY_INITIAL_CLASS = 'opacity-0'

export const Modal = ({
    children,
    title,
    size = DEFAULT_MODAL_SIZE,
    stackedButtons,
    footer,
    headerComponent,
    canBeClosed = true,
    isModalVisible = false,
    setModalVisibility,
    hasBackdrop = true,
    fullContentWidth = false,
    onShow,
    onHide,
    ...props
}: ModalProps) => {
    const [modalDialogRef, setModalDialogRef] = useState<RefObject<HTMLDivElement> | null>(null)
    const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null)
    const isFirstRender = useIsFirstRender()
    const backdropElement = createRef<HTMLDivElement>()

    useEffect(() => {
        setModalRoot(document.getElementById('modal-root'))
    }, [])

    const toggleModal = (): void => {
        setModalVisibility((prevValue) => !prevValue)
    }

    useEffect(() => {
        if (modalDialogRef?.current) return
        setModalDialogRef(createRef())
    }, [])

    const handleOutsideClick = (): void => {
        if (isModalVisible && canBeClosed) setModalVisibility(false)
    }

    useOnClickOutside(modalDialogRef!, handleOutsideClick)

    useEffect(() => {
        if (!isFirstRender) {
            if (isModalVisible && onShow) {
                onShow()
            } else if (!isModalVisible && onHide) {
                onHide()
            }
        }
    }, [isModalVisible])

    useEffect(() => {
        // if the modal is visible, remove the initial opacity and scale classes in the next animation frame
        // to trigger the transition
        if (isModalVisible && modalDialogRef?.current && backdropElement?.current) {
            requestAnimationFrame(() => {
                if (backdropElement.current && modalDialogRef?.current) {
                    backdropElement.current.classList.remove(MODAL_OPACITY_INITIAL_CLASS)
                    modalDialogRef.current.classList.remove(MODAL_OPACITY_INITIAL_CLASS)
                    modalDialogRef.current.classList.remove(MODAL_SCALE_INITIAL_CLASS)
                }
            })
        }
    }, [isModalVisible, backdropElement, modalDialogRef])

    return (
        <>
            {modalRoot &&
                createPortal(
                    <>
                        {isModalVisible && (
                            <div
                                {...props}
                                className="modal fixed top-0 left-0 right-0 bottom-0 z-50 p-6 inline-flex justify-content items-center max-w"
                            >
                                {hasBackdrop && (
                                    <div
                                        ref={backdropElement}
                                        className={`transition-opacity duration-600 ease-out ${MODAL_OPACITY_INITIAL_CLASS}`}
                                    >
                                        <Backdrop className="modal__backdrop" />
                                    </div>
                                )}
                                <div
                                    ref={modalDialogRef}
                                    className={`modal__dialog transition-all ease-out duration-600 ${MODAL_SCALE_INITIAL_CLASS} ${MODAL_OPACITY_INITIAL_CLASS} bg-shade-100 shadow-drop-lg w-full ${size} m-auto rounded max-h-full flex`}
                                >
                                    <div className={`modal__content max-h-full flex flex-col w-full ${size}`}>
                                        <div
                                            className={`modal__header flex items-center w-full pt-4 pl-6 pr-4 pb-4 max-w ${
                                                headerComponent ? 'border-b border-b-shade-200 mb-4' : ''
                                            }`}
                                        >
                                            {title && (
                                                <Text className="mr-4" type={TextTypes.HEADING_3}>
                                                    {title}
                                                </Text>
                                            )}
                                            {headerComponent && headerComponent}
                                            {canBeClosed && (
                                                <Button
                                                    onClick={toggleModal}
                                                    variant={ButtonVariants.QUIET}
                                                    className="ml-auto"
                                                    icon={<CloseIcon />}
                                                    data-testid="close-modal-button"
                                                />
                                            )}
                                        </div>
                                        {children && (
                                            <div
                                                className={`modal__body ${
                                                    fullContentWidth ? '' : 'px-6'
                                                } pt-2 pb-4 text-text-default overflow-y-auto overflow-x-hidden`}
                                            >
                                                {children}
                                            </div>
                                        )}
                                        {footer && (
                                            <div
                                                className={`modal__footer flex ${
                                                    stackedButtons ? 'flex-col gap-2' : 'gap-4'
                                                } justify-end items-center pt-2 pb-6 px-6`}
                                            >
                                                {footer}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>,
                    modalRoot,
                )}
        </>
    )
}
